import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ApiError, AuthError, isApiError, createAuthError } from '@/lib/types';
import { setUserContext, clearUserContext, trackUserAction } from '@/lib/sentry';
import IdleTimeoutManager from '@/components/IdleTimeoutManager';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, firstName?: string, lastName?: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    let initialLoadComplete = false;
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Set Sentry user context when user signs in/out
        if (session?.user) {
          setUserContext({
            id: session.user.id,
            email: session.user.email,
            role: session.user.user_metadata?.role || 'user',
          });
          trackUserAction('sign_in', 'auth', { userId: session.user.id });
        } else {
          clearUserContext();
          trackUserAction('sign_out', 'auth');
        }
        
        if (initialLoadComplete) {
          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      initialLoadComplete = true;
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    trackUserAction('attempt_sign_in', 'auth', { email });
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        const authError = createAuthError(error.message, {
          status: error.status,
          field: error.message.toLowerCase().includes('email') ? 'email' : 
                 error.message.toLowerCase().includes('password') ? 'password' : undefined
        });
        
        toast({
          title: "Sign In Failed",
          description: authError.message,
          variant: "destructive",
        });
        
        return { error: authError };
      }
      
      return { error: null };
    } catch (error) {
      const authError = createAuthError(
        isApiError(error) ? error.message : "An unexpected error occurred"
      );
      
      toast({
        title: "Sign In Failed",
        description: authError.message,
        variant: "destructive",
      });
      
      return { error: authError };
    }
  };

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    trackUserAction('attempt_sign_up', 'auth', { email });
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName,
            last_name: lastName,
          }
        }
      });
      
      if (error) {
        const authError = createAuthError(error.message, {
          status: error.status,
          field: error.message.toLowerCase().includes('email') ? 'email' : 
                 error.message.toLowerCase().includes('password') ? 'password' : undefined
        });
        
        toast({
          title: "Sign Up Failed",
          description: authError.message,
          variant: "destructive",
        });
        
        return { error: authError };
      } else {
        toast({
          title: "Account Created",
          description: "Please check your email to verify your account.",
        });
      }
      
      return { error: null };
    } catch (error) {
      const authError = createAuthError(
        isApiError(error) ? error.message : "An unexpected error occurred"
      );
      
      toast({
        title: "Sign Up Failed",
        description: authError.message,
        variant: "destructive",
      });
      
      return { error: authError };
    }
  };

  const signOut = async () => {
    trackUserAction('attempt_sign_out', 'auth');
    try {
      await supabase.auth.signOut();
      toast({
        title: "Signed Out",
        description: "You have been successfully signed out.",
      });
    } catch (error) {
      const errorMessage = isApiError(error) ? error.message : "An error occurred while signing out";
      toast({
        title: "Sign Out Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const value = useMemo(() => ({
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  }), [user, session, loading]);

  return (
    <AuthContext.Provider value={value}>
      <IdleTimeoutManager />
      {children}
    </AuthContext.Provider>
  );
};